import { type ClipboardEvent, type KeyboardEvent, useMemo, useRef, useState } from 'react'

import {
  appendTags,
  fitEntries,
  parseTagText,
  splitTypedText,
  type TagEntry,
  tagKey,
} from '../../utils/tagText'
import { Badge } from './badge'
import { Button } from './button'
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  useComboboxAnchor,
} from './combobox'
import { filterOptions } from './searchable-select'

const MAX_SUGGESTIONS = 50
const PASTE_SEPARATORS = /[\r\n;,%]/

export enum TagInputAppearance {
  /** The combobox field: a glow ring on focus and when invalid. */
  COMBOBOX = 'combobox',
  /** Matches Input: the border alone shows focus and errors, with Input's padding, and no Enter tip. */
  INPUT = 'input',
}

const APPEARANCE_CLASS: Record<TagInputAppearance, string | undefined> = {
  [TagInputAppearance.COMBOBOX]: undefined,
  [TagInputAppearance.INPUT]: 'px-4 focus-within:ring-0 has-aria-invalid:ring-0',
}

export interface SuggestionHint {
  name: string
  hint: string
}

export interface SuggestionGroup {
  label: string
  names: string[]
  /** Shown as a small chip on the named suggestion. */
  hints?: SuggestionHint[]
}

interface MatchGroup {
  value: string
  items: string[]
}

interface TagInputProps {
  id?: string
  value: string[]
  onChange: (next: string[]) => void
  suggestions: string[]
  /** Shows the dropdown under these headings, each with a "Select all", instead of as one flat list. */
  suggestionGroups?: SuggestionGroup[]
  /** Names whose commas never split a tag, such as FMCSA cargo types. */
  unsplittable?: string[]
  placeholder?: string
  appearance?: TagInputAppearance
  /** Past this many the field shows an error; adding is never blocked. */
  maxTags?: number
  /** The error shown past `maxTags`, given how many to remove. */
  overLimitMessage?: (excess: number) => string
  maxLength?: number
  disabled?: boolean
  'aria-label'?: string
  'aria-invalid'?: boolean
  /** When set, a paste in which any part ends in "NN%" comes here instead of onChange. */
  onSharesFromPaste?: (entries: TagEntry[]) => void
}

/** Tags you type, paste or pick from suggestions, which stay listed and show as selected; Enter, a line break, a semicolon or a comma adds one. */
export function TagInput({
  id,
  value,
  onChange,
  suggestions,
  suggestionGroups,
  appearance = TagInputAppearance.COMBOBOX,
  unsplittable,
  placeholder,
  maxTags = 10,
  overLimitMessage = (excess) => `You can add up to ${maxTags}. Remove ${excess} to continue.`,
  maxLength = 100,
  disabled,
  'aria-label': ariaLabel,
  'aria-invalid': ariaInvalid,
  onSharesFromPaste,
}: TagInputProps) {
  const anchor = useComboboxAnchor()
  const [text, setText] = useState('')
  const highlighted = useRef<string | undefined>(undefined)
  const list = useRef<HTMLDivElement>(null)
  const [openSide, setOpenSide] = useState<'top' | 'bottom' | null>(null)
  const protectedNames = useMemo(() => [...suggestions, ...(unsplittable ?? [])], [suggestions, unsplittable])
  const overLimit = value.length > maxTags

  const matches = useMemo(() => matching(suggestions, text).slice(0, MAX_SUGGESTIONS), [suggestions, text])
  const groupMatches = useMemo<MatchGroup[] | null>(() => {
    if (!suggestionGroups) return null
    return suggestionGroups
      .map((group) => ({ value: group.label, items: matching(group.names, text) }))
      .filter((group) => group.items.length > 0)
  }, [suggestionGroups, text])

  const showEnterTip = appearance === TagInputAppearance.COMBOBOX

  const groupNames = useMemo(
    () => new Map((suggestionGroups ?? []).map((group) => [group.label, group.names])),
    [suggestionGroups]
  )

  const hintByName = useMemo(
    () =>
      new Map(
        (suggestionGroups ?? []).flatMap((group) =>
          (group.hints ?? []).map(({ name, hint }) => [tagKey(name), hint])
        )
      ),
    [suggestionGroups]
  )

  const selectedKeys = useMemo(() => new Set(value.map(tagKey)), [value])

  function remove(names: string[]) {
    const drop = new Set(names.map(tagKey))
    onChange(value.filter((tag) => !drop.has(tagKey(tag))))
  }

  function renderGroupedItem(item: string) {
    const hint = hintByName.get(tagKey(item))
    return (
      <ComboboxItem
        key={item}
        value={item}
        className="flex-wrap rounded-none not-last:shadow-[inset_0_-1px_0_var(--color-ink-100)]"
      >
        {/* The name grows, so the hint sits right on a shared line and starts left once it wraps. */}
        <span className="grow">{item}</span>
        {hint && <Badge variant="warning">Hint: {hint}</Badge>}
      </ComboboxItem>
    )
  }

  function add(names: string[]) {
    const next = appendTags(value, names, { maxTags: Number.POSITIVE_INFINITY, maxLength })
    if (next.length !== value.length) onChange(next)
  }

  function addText(raw: string) {
    add(parseTagText(raw, { protectedNames, maxLength }).map((entry) => entry.name))
  }

  function commitText() {
    if (!text.trim()) return
    addText(text)
    setText('')
  }

  function handleValueChange(next: string[], details: { reason: string }) {
    // Base UI clears every chip on Escape; here tags only go one at a time.
    if (details.reason === 'escape-key') return
    if (next.length > value.length) add(next.slice(value.length))
    else onChange(next)
  }

  function handleInputValueChange(next: string, details: { reason: string }) {
    if (details.reason !== 'input-change') {
      setText(next)
      return
    }
    const { complete, rest } = splitTypedText(next, protectedNames)
    if (complete) addText(complete)
    setText(rest)
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return
    if (highlighted.current !== undefined || !text.trim()) return
    event.preventDefault()
    commitText()
  }

  function handlePaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text/plain')
    if (!PASTE_SEPARATORS.test(pasted)) return
    event.preventDefault()
    const input = event.currentTarget
    const before = text.slice(0, input.selectionStart ?? text.length)
    const after = text.slice(input.selectionEnd ?? text.length)
    const entries = parseTagText(before + pasted + after, { protectedNames, withShares: true, maxLength })
    setText('')
    if (onSharesFromPaste && entries.some((entry) => entry.percentage !== null)) {
      onSharesFromPaste(fitEntries(value, entries, Number.POSITIVE_INFINITY))
      return
    }
    add(entries.map((entry) => entry.name))
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Combobox
        multiple
        value={value}
        onValueChange={handleValueChange}
        inputValue={text}
        onInputValueChange={handleInputValueChange}
        items={groupMatches ?? matches}
        filteredItems={groupMatches ?? matches}
        isItemEqualToValue={(item, tag) => tagKey(item) === tagKey(tag)}
        onOpenChange={(open) => {
          if (!open) {
            setOpenSide(null)
            return
          }
          // Base UI scrolls to the last selected item on open; wait out its effects, then go to the top.
          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              if (!list.current) return
              list.current.scrollTop = 0
              // Keep the side it opened on, so growing chips don't flip it mid-pick.
              const side = list.current.closest('[data-side]')?.getAttribute('data-side')
              setOpenSide(side === 'top' ? 'top' : 'bottom')
            })
          )
        }}
        onItemHighlighted={(item) => {
          highlighted.current = item
        }}
        disabled={disabled}
      >
        <ComboboxChips
          ref={anchor}
          data-disabled={disabled || undefined}
          className={APPEARANCE_CLASS[appearance]}
        >
          {value.map((tag) => (
            <ComboboxChip key={tag} removeLabel={`Remove ${tag}`}>
              {tag}
            </ComboboxChip>
          ))}
          <ComboboxChipsInput
            id={id}
            aria-label={ariaLabel}
            aria-invalid={ariaInvalid || overLimit || undefined}
            placeholder={value.length === 0 ? placeholder : undefined}
            className="placeholder:text-ink-300 bg-transparent"
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={commitText}
          />
        </ComboboxChips>
        <ComboboxContent
          className={showEnterTip ? undefined : 'data-empty:hidden'}
          anchor={anchor}
          side={openSide ?? 'bottom'}
          collisionAvoidance={openSide ? { side: 'none' } : undefined}
        >
          {showEnterTip && <ComboboxEmpty>Press Enter to add it.</ComboboxEmpty>}
          <ComboboxList ref={list}>
            {groupMatches
              ? (group: MatchGroup) => (
                  <ComboboxGroup key={group.value} items={group.items}>
                    <div className="bg-ink-50 flex items-center justify-between gap-2 rounded-sm pr-2">
                      <ComboboxLabel>{group.value}</ComboboxLabel>
                      <GroupSelectButton
                        label={group.value}
                        names={groupNames.get(group.value) ?? []}
                        selectedKeys={selectedKeys}
                        onSelect={(names) => {
                          add(names)
                          setText('')
                        }}
                        onDeselect={(names) => {
                          remove(names)
                          setText('')
                        }}
                      />
                    </div>
                    <ComboboxCollection>{renderGroupedItem}</ComboboxCollection>
                  </ComboboxGroup>
                )
              : renderItem}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
      {overLimit && (
        <p role="alert" className="text-destructive text-sm">
          {overLimitMessage(value.length - maxTags)}
        </p>
      )}
    </div>
  )
}

function matching(names: string[], text: string): string[] {
  return filterOptions(
    names.map((name) => ({ value: name, label: name })),
    text
  ).map((option) => option.value)
}

function renderItem(item: string) {
  return (
    <ComboboxItem key={item} value={item}>
      {item}
    </ComboboxItem>
  )
}

function GroupSelectButton({
  label,
  names,
  selectedKeys,
  onSelect,
  onDeselect,
}: {
  label: string
  names: string[]
  selectedKeys: Set<string>
  onSelect: (names: string[]) => void
  onDeselect: (names: string[]) => void
}) {
  const allSelected = names.length > 0 && names.every((name) => selectedKeys.has(tagKey(name)))
  return (
    <Button
      type="button"
      variant="link"
      size="xs"
      className="text-brand-800 hover:text-brand-900 h-auto px-0 font-semibold"
      // Keeps focus in the input so the list stays open.
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => (allSelected ? onDeselect(names) : onSelect(names))}
    >
      {allSelected ? 'Deselect' : 'Select'} all {label.toLowerCase()}
    </Button>
  )
}
